import { Plus32 } from '@packages/themes/icons';
import { useIntl } from 'react-intl';
import {
  Box,
  InlineNotification,
  MuiIconButton,
  Notification,
  Typography,
  useDeviceType,
} from '@packages/shared';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { AddRatingDialog } from './AddRatingDialog';
import { ratingMessages } from './messages';

type AddRatingProps = {
  /**
   * Product component to render in the dialog
   */
  productOverview: ReactNode;
};

/**
 * Add rating dashed button with multi step form in a dialog
 */
export const AddRating = ({ productOverview }: AddRatingProps) => {
  const [dialogOpened, setDialogOpened] = useState(false);
  const [openSuccessNotification, setOpenSuccessNotification] = useState(false);
  const [openErrorNotification, setOpenErrorNotification] = useState(false);
  const { isMobile } = useDeviceType();
  const { formatMessage } = useIntl();

  const handleSuccess = () => {
    setOpenSuccessNotification(true);
    setDialogOpened(false);
  };

  return (
    <>
      <Notification
        open={openSuccessNotification}
        severity="success"
        autoHideDuration={5000}
        onClose={() => setOpenSuccessNotification(false)}
      >
        {formatMessage(ratingMessages.newRatingSubmitSuccessNotification)}
      </Notification>
      <Notification
        open={openErrorNotification}
        severity="error"
        autoHideDuration={5000}
        onClose={() => setOpenErrorNotification(false)}
      >
        {formatMessage(ratingMessages.newRatingSubmitErrorNotification)}
      </Notification>
      {dialogOpened && (
        <AddRatingDialog
          open={dialogOpened}
          title={formatMessage(ratingMessages.dialogTitle)}
          handleClose={() => setDialogOpened(false)}
          contentProps={{
            productOverview,
            onSuccess: handleSuccess,
            onError: () => setOpenErrorNotification(true),
          }}
        />
      )}
      {openSuccessNotification && isMobile && (
        <InlineNotification severity="info">
          {formatMessage(ratingMessages.newRatingSubmitSuccess)}
        </InlineNotification>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.25,
          width: '100%',
          height: 150,
          border: '1px dashed',
          borderColor: 'grey.main',
          borderRadius: '3px',
        }}
      >
        <MuiIconButton
          onClick={() => setDialogOpened(true)}
          sx={{ bgcolor: 'white' }}
          aria-label={formatMessage(ratingMessages.buttonLabel)}
        >
          <Plus32 sx={{ width: 48, height: 48 }} />
        </MuiIconButton>
        <Typography textAlign="center">{formatMessage(ratingMessages.buttonLabel)}</Typography>
      </Box>
    </>
  );
};
